/* Product analytics — Microsoft Clarity.
 *
 * Sentry (`src/telemetry.js`) answers "what threw". This answers the other
 * half: what someone was *doing* — where they gave up in the builder, which
 * tab they never found, whether the Matchups run is worth the wait. Neither
 * substitutes for the other, which is why there are two files.
 *
 * ---------------------------------------------------------------------------
 * THIS ONE COSTS SOMETHING, AND THE COST BELONGS IN THE OPEN.
 *
 * Clarity has no bundled build. `Clarity.init` injects a `<script>` pointing
 * at `https://www.clarity.ms/tag/<id>`, so the policy has to allow it — and
 * `script-src 'self' https://www.clarity.ms` means Microsoft may execute
 * arbitrary JavaScript on a page that holds a Supabase session in
 * localStorage. That is a real widening of #17, not a formality, and it is why
 * this landed as its own change rather than riding along with Sentry: the
 * diff to the CSP is the thing to judge, and it should be judgeable alone.
 *
 * The Sentry SDK is bundled from npm precisely so it did *not* need this.
 *
 * Two consequences that are now facts rather than opinions:
 *
 *   - **Session replay records the DOM.** Anything on screen can end up in a
 *     recording. `MASK_ATTR` is applied to the account chip, because that is
 *     where a signed-in user's email is rendered and an email in a third
 *     party's session recording is a different thing from a click heatmap.
 *   - **The privacy policy in #40 stopped being optional.** Clarity sets
 *     cookies and records sessions. That item was already flagged; this is
 *     what makes it due.
 *
 * Turning it off is unsetting `VITE_CLARITY_ID` — but note that the CSP entry
 * stays either way, because the policy is a static file. Removing the
 * permission means removing those two hosts from index.html.
 * ---------------------------------------------------------------------------
 */

/** Put this on anything that must never appear in a session recording. */
export const MASK_ATTR = 'data-clarity-mask';

let started = false;

/** Test seam — module state would otherwise leak between tests. */
export function _resetAnalytics() { started = false; }

/** Has Clarity actually been started in this page? */
export function analyticsActive() { return started; }

/**
 * Start Clarity, if there is a project to send to.
 *
 * @param {object} [opts]
 *   id     Clarity project id; falsy disables everything
 *   load   override the module import, for tests
 * @returns {Promise<boolean>} whether it actually started
 */
export async function initAnalytics({
  id = import.meta.env?.VITE_CLARITY_ID,
  load = () => import('@microsoft/clarity'),
} = {}) {
  if (!id || started) return false;

  /* A project id is a short alphanumeric token. Validated because it is
   * interpolated straight into a script URL by the vendor's own injector, and
   * a build-time variable is not a place to be relaxed about that. */
  if (!/^[a-z0-9]{1,32}$/i.test(String(id))) {
    console.warn('Ignoring a VITE_CLARITY_ID that is not a project id.');
    return false;
  }

  let Clarity;
  try {
    const mod = await load();
    Clarity = mod?.default ?? mod;
    Clarity.init(String(id));
  } catch {
    /* Analytics failing must never be the reason the app does not work. */
    return false;
  }

  started = true;
  return true;
}

/**
 * Note something worth counting.
 *
 * Deliberately narrow: an event *name*, never a payload. There is no way to
 * pass a deck name, a card id or an email through this, which is the point —
 * the useful question is "how often does anyone export an image", and the
 * shape of this function is what stops it drifting into "who exported what".
 */
export function trackEvent(name) {
  if (!started || typeof name !== 'string' || !name) return;
  try {
    globalThis.clarity?.('event', name);
  } catch { /* never throw into the app */ }
}
